import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useGetInvitationDetails,
  userInvitationConfirmation,
} from "../../api/userInfo";
import bg_image from "../../assets/backgroundImages/company_bg.jpg";
import { WidgetLoading } from "../../components/ui";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";

const InvitationPage = ({ hasAccount = true }) => {
  const { t } = useTranslation("main");
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const formContainerRef = useRef(null);
  const headerRef = useRef(null);
  const domain = window?.location?.hostname;
  const [formContainerHeight, setFormContainerHeight] = useState(560);
  const [details, setDetails] = useState("");
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    firstName: "",
    lastName: "",
  });
  const [terms, setTerms] = useState(false);
  const [policy, setPolicy] = useState(false);
  const { invitation, isLoadingInvitation, isErrorInvitation } =
    useGetInvitationDetails(pathname, search);

  const isUrlFrasers = domain.includes("frasers");

  function validateEmail(email) {
    var re = /\S+@\S+\.\S+/;
    return re.test(email);
  }

  function validatePassword(password) {
    const errors = [];

    // Check minimum length
    if (password.length < 8) {
      errors.push("at least 8 characters long");
    }

    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push("at least one uppercase letter");
    }

    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push("at least one lowercase letter");
    }

    // Check for number
    if (!/\d/.test(password)) {
      errors.push("at least one number");
    }

    // Check for special character
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push("at least one special character");
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  useEffect(() => {
    if (invitation && invitation?.answer != "pending" && !isLoadingInvitation) {
      navigate("/login");
    }
  }, [invitation]);

  useEffect(() => {
    const invitation_details = {
      email: new URLSearchParams(search)?.get("email"),
      invite_id: pathname?.split("/")?.slice(-2)?.[0],
    };

    if (validateEmail(invitation_details?.email)) {
      setDetails(invitation_details);
      // Pre-fill the email in the form (only for users without account)
      if (!hasAccount) {
        setFormData((prev) => ({
          ...prev,
          email: invitation_details.email,
        }));
      }
    } else {
      alert("Invalid invitation link");
      navigate("/login");
    }

    return () => {
      // cleanup
      setDetails("");
    };
  }, [hasAccount]);

  //   detect screen resize and update formContainerHeight accordingly
  useEffect(() => {
    const handleResize = () => {
      if (formContainerRef.current) {
        setFormContainerHeight(formContainerRef.current.clientHeight);
      }
    };

    window.addEventListener("resize", handleResize);

    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      // window.removeEventListener("scroll", handleResize);
    };
  }, []);

  useEffect(() => {
    const formContainerHeight = formContainerRef.current.clientHeight;
    setFormContainerHeight(formContainerHeight);
  }, [formContainerHeight]);

  //   _________________________________________________________________________

  const handleAcceptInvitation = async () => {
    setFormContainerHeight(560);
    if (hasAccount) {
      // For users with account: call API immediately
      await acceptInvitation(true);
    } else {
      // For users without account: show registration form
      setShowRegistrationForm(true);
    }
  };

  const acceptInvitation = async (accepted) => {
    try {
      let request = {
        is_accept: accepted,
        email: invitation.email,
        plant: invitation.plant,
      };

      const response = await userInvitationConfirmation(
        invitation?.id,
        request
      );

      if (accepted) {
        if (response?.error) {
          alert(`${response?.error}`);
          navigate("/login");
          return;
        }
        if (response?.data) {
          switch (response?.data?.detail) {
            case "Registration is required":
              // For users without account, show the registration form
              setShowRegistrationForm(true);
              break;
            case "error while verifying invitation.":
              alert("error while verifying invitation, please try again");
              break;
            default:
              navigate("/login");
              break;
          }
        } else {
          //   alert(response?.error);
          navigate("/login");
        }
      } else if (!accepted && response?.data) {
        navigate("/login");
      } else {
        alert(response?.error);
        navigate("/login");
      }
    } catch (error) {
      alert("Something went wrong, please try again");
    }
  };

  const handleRejectInvitation = async () => {
    await acceptInvitation(false);
  };

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Real-time password validation
    if (name === "password" && value.length > 0) {
      const passwordValidation = validatePassword(value);
      if (!passwordValidation.isValid) {
        setFormErrors((prev) => ({
          ...prev,
          password: `Password must be ${passwordValidation.errors.join(", ")}.`,
        }));
      }
    }

    // Clear confirm password error when either password field changes
    if (
      (name === "password" || name === "confirmPassword") &&
      formErrors.confirmPassword
    ) {
      setFormErrors((prev) => ({
        ...prev,
        confirmPassword: "",
      }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setFormErrors({});

    let hasErrors = false;
    const newErrors = {};

    // Validate password strength
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      newErrors.password = `Password must be ${passwordValidation.errors.join(
        ", "
      )}.`;
      hasErrors = true;
    }

    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword =
        "Passwords do not match. Please check and try again.";
      hasErrors = true;
    }

    // If there are validation errors, show them and return
    if (hasErrors) {
      setFormErrors(newErrors);
      return;
    }

    try {
      let request = {
        is_accept: true,
        email: invitation.email,
        plant: invitation.plant,
        username: formData.email,
        password: formData.password,
        phone: formData.phoneNumber,
        first_name: formData.firstName,
        last_name: formData.lastName,
        is_register: true,
      };

      const response = await userInvitationConfirmation(
        invitation?.id,
        request
      );

      if (response?.data?.detail) {
        switch (response?.data?.detail) {
          case "Registration is required":
            navigate("/signup");
            setShowRegistrationForm(true);
            break;
          case "error while verifying invitation.":
            alert("error while verifying invitation, please try again");
            break;
          case "Available for sign in":
            navigate("/verify");
          default:
            navigate("/login");
            break;
        }
      } else if (response?.data) {
        navigate("/verify");
      } else {
        alert(response?.error);
        return;
      }
    } catch (error) {
      alert("Something went wrong, please try again");
    }
  };

  const handleFormCancel = () => {
    // Cancel means reject the invitation
    setShowRegistrationForm(false);
    handleRejectInvitation();
  };

  return (
    <div
      className="flex relative justify-center items-center w-full h-screen bg-no-repeat bg-cover bg-image"
      style={{ backgroundImage: `url(${bg_image})` }}
    >
      {
        //   (!invitation && !isErrorInvitation)
        invitation && isErrorInvitation ? (
          <div>
            <WidgetLoading />
            <p className="mt-8 text-white/75">Loading Invitation</p>
          </div>
        ) : isErrorInvitation && !invitation ? (
          <div>
            <WidgetLoading />
            <p className="mt-8 text-white/75">
              An error occurred while loading invitation
            </p>
          </div>
        ) : (
          <div className="flex overflow-hidden absolute inset-0 z-20 justify-center items-center w-full h-screen">
            <div
              ref={formContainerRef}
              className="p-4 mx-auto max-w-lg text-center bg-white rounded h-max max-h-[90vh] md:max-w-lg min-w-[450px]"
            >
              {!hasAccount && showRegistrationForm ? (
                <>
                  {/* header */}
                  <div ref={headerRef} className="border-b border-gray-200">
                    <h1 className="text-3xl font-bold text-center text-vekin">
                      Create Account
                    </h1>

                    <p className="mb-4 pt-2">
                      Complete your registration to join{" "}
                      <span className="font-bold">
                        {invitation?.plant_name}
                      </span>
                    </p>
                  </div>

                  {/* form */}
                  <div className="full">
                    <form onSubmit={handleFormSubmit} className="h-full">
                      <div
                        style={{
                          height: `${
                            formContainerHeight -
                            (headerRef?.current?.clientHeight + 105)
                          }px`,
                          maxHeight: `560px`,
                        }}
                        className="space-y-4 p-4 my-2 py-2 overflow-y-auto"
                      >
                        <div className="text-left">
                          <label
                            htmlFor="email"
                            className="block mb-1 text-sm font-medium text-gray-700"
                          >
                            Email<span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleFormInputChange}
                            className="p-2 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-vekin focus:border-transparent"
                            required
                            readOnly
                          />
                        </div>
                        <div className="text-left">
                          <label
                            htmlFor="firstName"
                            className="block mb-1 text-sm font-medium text-gray-700"
                          >
                            First Name<span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleFormInputChange}
                            className="p-2 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-vekin focus:border-transparent"
                            required
                          />
                        </div>
                        <div className="text-left">
                          <label
                            htmlFor="lastName"
                            className="block mb-1 text-sm font-medium text-gray-700"
                          >
                            Last Name<span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleFormInputChange}
                            className="p-2 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-vekin focus:border-transparent"
                            required
                          />
                        </div>
                        <div className="text-left">
                          <label
                            htmlFor="password"
                            className="block mb-1 text-sm font-medium text-gray-700"
                          >
                            Password<span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleFormInputChange}
                            className={`p-2 w-full rounded-md border focus:ring-2 focus:ring-vekin focus:border-transparent ${
                              formErrors.password
                                ? "border-red-500 focus:ring-red-500"
                                : "border-gray-300"
                            }`}
                            required
                            minLength="8"
                          />
                          {formErrors.password && (
                            <p className="mt-1 text-sm text-red-600">
                              {formErrors.password}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            Password must be at least 8 characters long and
                            include a mix of uppercase, lowercase, numbers, and
                            special characters. It cannot be a commonly used
                            password.
                          </p>
                        </div>
                        <div className="text-left">
                          <label
                            htmlFor="confirmPassword"
                            className="block mb-1 text-sm font-medium text-gray-700"
                          >
                            Confirm Password
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleFormInputChange}
                            className={`p-2 w-full rounded-md border focus:ring-2 focus:ring-vekin focus:border-transparent ${
                              formErrors.confirmPassword
                                ? "border-red-500 focus:ring-red-500"
                                : "border-gray-300"
                            }`}
                            required
                            minLength="8"
                          />
                          {formErrors.confirmPassword && (
                            <p className="mt-1 text-sm text-red-600">
                              {formErrors.confirmPassword}
                            </p>
                          )}
                        </div>
                        <div className="text-left">
                          <label
                            htmlFor="phoneNumber"
                            className="block mb-1 text-sm font-medium text-gray-700"
                          >
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleFormInputChange}
                            className="p-2 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-vekin focus:border-transparent"
                            //   required
                          />
                        </div>
                      </div>

                      {/* button submit and cancel */}
                      <div className="flex gap-4 justify-center pt-4 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={handleFormCancel}
                          className="gap-2 items-center p-2 px-4 w-32 text-center text-red-400 rounded-md border border-red-300 hover:shadow-md"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="gap-2 items-center p-2 px-4 w-32 text-center text-white rounded-md bg-vekin hover:shadow-md"
                        >
                          Submit
                        </button>
                      </div>
                    </form>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="text-3xl font-bold text-center text-vekin">
                    Invitation
                  </h1>
                  <div className="p-4 m-4 pb-2 mb-3">
                    <p>
                      You have been invited to join{" "}
                      <span className="font-bold">
                        {invitation?.plant_name}
                      </span>{" "}
                      <br></br>from{" "}
                      <span className="font-bold">
                        {invitation?.company_name} company
                      </span>
                    </p>
                  </div>

                  {/* checkbox for terms and conditions and privacy policy */}
                  <div className="flex flex-col gap-4 justify-start items-start pb-4 px-5">
                    <fieldset>
                      <legend className="sr-only">Checkbox variants</legend>

                      {/* terms and conditions */}
                      <div className="flex items-start">
                        <input
                          id="termsCondition"
                          type="checkbox"
                          defaultValue={terms}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setTerms(isChecked);
                          }}
                          className="w-4 h-4 rounded accent-emerald-700 focus:ring-3 focus:ring-vekin"
                        />
                        <label
                          for="termsCondition"
                          className="ml-2 text-sm font-medium text-gray-900"
                        >
                          {t("register.i_agree")}{" "}
                          <a
                            href={
                              isUrlFrasers
                                ? "/terms&policy/frasers/ESG_Terms and Conditions (TH).pdf"
                                : "/terms&policy/vekin/thai_terms_of_services_of_carbon_wallet_platform.pdf"
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="text-vekin underline"
                          >
                            {t("register.terms_and_conditions")}
                          </a>
                        </label>
                      </div>

                      {/* privacy policy */}
                      <div className="flex items-start">
                        <input
                          id="privacyPolicy"
                          type="checkbox"
                          defaultValue={policy}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setPolicy(isChecked);
                          }}
                          className="w-4 h-4 rounded accent-emerald-700 focus:ring-3 focus:ring-vekin"
                        />
                        <label
                          for="privacyPolicy"
                          className="ml-2 text-sm font-medium text-gray-900"
                        >
                          {t("register.i_agree")}{" "}
                          <a
                            href={
                              isUrlFrasers
                                ? "/terms&policy/frasers/ESG_Privacy Notice of ESG Management System (TH).pdf"
                                : "/terms&policy/vekin/thai_terms_of_services_of_carbon_wallet_platform.pdf"
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="text-vekin underline"
                          >
                            {t("register.privacy_policy")}
                          </a>
                        </label>
                      </div>
                      {/* {errors.terms && (<p className="text-red-400 text-xs mt-0">{errors.terms}</p>)} */}
                    </fieldset>
                  </div>

                  <div className="flex gap-8 justify-center px-5">
                    {/* <button
                      onClick={handleRejectInvitation}
                      className="gap-2 items-center p-2 px-4 w-40 text-center text-red-400 rounded-md border border-red-300 hover:shadow-md"
                    >
                      Reject
                    </button> */}

                    <button
                      disabled={!terms || !policy}
                      onClick={handleAcceptInvitation}
                      className={`gap-2 items-center p-3 px-5 text-center text-white rounded-md bg-vekin ${cn(
                        !terms || !policy
                          ? "opacity-40 cursor-not-allowed hover:shadow-none"
                          : "hover:shadow-md"
                      )}`}
                    >
                      Accept Invitation
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )
      }
    </div>
  );
};

// Wrapper components for different use cases
export const InvitationPageWithAccount = () => (
  <InvitationPage hasAccount={true} />
);
export const InvitationPageNoAccount = () => (
  <InvitationPage hasAccount={false} />
);

export default InvitationPage;
